// src/components/checkout/SuccessHandler.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { updateSubscription } from '../../services/subscriptionService';

/**
 * SuccessHandler component - processes successful Stripe checkout sessions
 * and updates the user's subscription status
 */
const SuccessHandler = () => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  
  useEffect(() => {
    // Parse query parameters
    const params = new URLSearchParams(window.location.search);
    const isCheckoutSuccess = params.get('checkout_success') === 'true';
    const sessionId = params.get('session_id');
    
    // If this isn't a checkout success redirect, skip processing
    if (!isCheckoutSuccess || !sessionId) {
      return;
    }
    
    const verifyCheckoutSession = async () => {
      try {
        setStatus('processing');
        
        // Make sure we have a user
        if (!currentUser) {
          throw new Error('User not authenticated');
        }
        
        console.log(`Processing successful checkout: ${sessionId} for user ${currentUser.uid}`);
        
        // Call our API to verify the session
        const response = await fetch('/api/stripe/verify-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionId,
            userId: currentUser.uid
          }),
        });
        
        // Safely parse the JSON response or handle text response
        let data;
        const responseText = await response.text();
        
        try {
          data = JSON.parse(responseText);
        } catch (jsonError) {
          console.error('Failed to parse response as JSON:', responseText);
          throw new Error(`Invalid response from server: ${responseText.substring(0, 100)}`);
        }
        
        if (!response.ok) {
          throw new Error(data.error || 'Failed to verify checkout session');
        }
        
        if (!data.success) {
          throw new Error('Checkout verification failed');
        }
        
        console.log('Checkout verified successfully:', data);
        
        // Update local subscription data
        updateSubscription({
          status: data.subscription.status,
          currentPeriodEnd: data.subscription.currentPeriodEnd,
          planId: data.subscription.planId,
          tier: 'pro'
        });
        
        // Clear URL parameters
        window.history.replaceState({}, document.title, '/account');
        
        setStatus('success');
        
        // Reload the page to reflect new Pro status
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } catch (err) {
        console.error('Error verifying checkout:', err);
        setError(err.message);
        setStatus('error');
      }
    };
    
    verifyCheckoutSession();
  }, [currentUser]);
  
  // If this isn't a checkout success page, don't render anything
  if (!new URLSearchParams(window.location.search).get('checkout_success')) {
    return null;
  }
  
  // If we haven't started processing yet, don't show anything
  if (!status) {
    return null;
  }
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        zIndex: 9999
      }}
    >
      {status === 'processing' && (
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800">
            Finalizing your Pro subscription...
          </h2>
        </div>
      )}
      
      {status === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-lg mx-auto shadow-lg">
          <h2 className="text-2xl font-bold text-green-700 mb-2">
            Congratulations! You're now a Pro subscriber!
          </h2>
          <p className="text-green-600">
            Your account has been upgraded. Redirecting you now...
          </p>
        </div>
      )}
      
      {status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-lg mx-auto shadow-lg">
          <h2 className="text-2xl font-bold text-red-700 mb-2">
            There was a problem activating your subscription
          </h2>
          <p className="text-red-600">
            {error || 'Please contact support if this issue persists.'}
          </p>
          <button 
            onClick={() => window.location.href = '/account'} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Return to Account
          </button>
        </div>
      )}
    </div>
  );
};

export default SuccessHandler;
